import { FC } from "react";
import {
  Box,
  Divider,
  FormControl,
  Grid2 as Grid,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { TagManager } from "components/TagManager";
import { StepLayoutContainer } from "components/Layout/StepLayoutContainer";
import { REQUIREMENT } from "helpers/constants";
import { Species } from "types/species";
import { Tag } from "types/tag";
import { Resource } from "types/resource";
import { REQUIREMENT_COMPETENCY_TYPE } from "pages/Requirements/CreateAndEdit/constants";
import { RelevantResourcesLink } from "pages/Requirements/CreateAndEdit/components/RelevantResourcesLink";
import { ParsedRequirementRow } from "../parseRequirementsCsv";
import { CompetencyLinksList } from "../components/CompetencyLinksList";
import {
  ProcessingSettings,
  RequirementImportType,
  ResolvedCompetencyLink,
  RUBRIC_COLUMN_COUNT,
} from "../types";

type Props = {
  requirementType: RequirementImportType;
  requirements: ParsedRequirementRow[];
  resolveCompetencyLinks: (names: string[]) => ResolvedCompetencyLink[];
  processing: ProcessingSettings;
  species: Species[];
  onProcessingChange: (settings: ProcessingSettings) => void;
  onBack: () => void;
  onNext: () => void;
};

export const ProcessingStep: FC<Props> = ({
  requirementType,
  requirements,
  resolveCompetencyLinks,
  processing,
  species,
  onProcessingChange,
  onBack,
  onNext,
}) => {
  const isEpaImport = requirementType === REQUIREMENT.REQUIREMENT_EPA;
  const isIterImport = requirementType === REQUIREMENT.REQUIREMENT_ITER;
  const linksCompetenciesFromCsv = isEpaImport || isIterImport;
  const isCompetency = requirementType === REQUIREMENT.REQUIREMENT_COMPETENCY;
  const showCompetencyType =
    requirementType === REQUIREMENT.REQUIREMENT_COMPETENCY ||
    requirementType === REQUIREMENT.REQUIREMENT_EPA;
  const showRubricLabels = isCompetency;
  const showSharedRequirementFields = !isIterImport;

  const updateField = <K extends keyof ProcessingSettings>(
    key: K,
    value: ProcessingSettings[K],
  ) => {
    onProcessingChange({ ...processing, [key]: value });
  };

  const updateRubricLabel = (
    field: "rubric_skill_level_labels" | "rubric_time_labels",
    index: number,
    value: string,
  ) => {
    const labels = [...processing[field]];
    labels[index] = value;
    updateField(field, labels);
  };

  const handleAddTag = (tag: Tag) => {
    onProcessingChange({ ...processing, tags: [...processing.tags, tag] });
  };

  const handleDeleteTag = (tagToRemove: Tag) => {
    onProcessingChange({
      ...processing,
      tags: processing.tags.filter((tag) => tag.tag_id !== tagToRemove.tag_id),
    });
  };

  const handleResourcesChange = (resources: Resource[]) => {
    onProcessingChange({ ...processing, resources });
  };

  return (
    <StepLayoutContainer
      title="Processing settings"
      description={
        isIterImport
          ? "Add tags and resources that apply to every ITER in this import."
          : showRubricLabels
            ? "These values apply to every requirement in this import. Rubric column times follow CSV order; customize skill level and time labels here."
            : "These values apply to every requirement in this import."
      }
      onClickBack={onBack}
      onClickNext={onNext}
      nextButtonText="Continue"
    >
      {linksCompetenciesFromCsv &&
        requirements.some((row) => row.competencies.length > 0) && (
        <Box mb={3} pb={3}>
          <Typography variant="h6" gutterBottom>
            Competencies to link
          </Typography>
          <Typography variant="body2" color="text.secondary" mb={2}>
            Existing competencies from the CSV that will be linked to each{" "}
            {isEpaImport ? "EPA" : "ITER"}. Org-exclusive names are matched first.
          </Typography>
          <Stack spacing={2}>
            {requirements.map((row) => (
              <Box key={row.row_key}>
                <Typography variant="subtitle2" gutterBottom>
                  {row.name}
                </Typography>
                <CompetencyLinksList
                  links={resolveCompetencyLinks(row.competencies)}
                />
              </Box>
            ))}
          </Stack>
          <Divider sx={{ mt: 3 }} />
        </Box>
      )}

      {showSharedRequirementFields && (
        <>
      <Typography variant="h6" mt={2} mb={2}>
        Shared requirement fields
      </Typography>
      <Grid container spacing={2}>
        <Grid size={{ xs: 6, sm: 3 }}>
          <TextField
            label="Hours"
            type="number"
            fullWidth
            value={processing.hour}
            onChange={(e) =>
              updateField("hour", Number.parseInt(e.target.value, 10) || 0)
            }
            inputProps={{ min: 0 }}
          />
        </Grid>
        <Grid size={{ xs: 6, sm: 3 }}>
          <TextField
            label="Minutes"
            type="number"
            fullWidth
            value={processing.minutes}
            onChange={(e) =>
              updateField("minutes", Number.parseInt(e.target.value, 10) || 0)
            }
            inputProps={{ min: 0, max: 59 }}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6 }}>
          <TextField
            label="Animal size"
            fullWidth
            value={processing.animal_size}
            onChange={(e) => updateField("animal_size", e.target.value)}
            placeholder={isCompetency ? "any" : "Optional"}
          />
        </Grid>
        {isCompetency && (
          <Grid size={{ xs: 12, sm: 6 }}>
            <FormControl fullWidth>
              <InputLabel>Species</InputLabel>
              <Select
                label="Species"
                value={processing.species_id || ""}
                onChange={(e) =>
                  updateField("species_id", e.target.value || null)
                }
              >
                <MenuItem value="">
                  <em>None</em>
                </MenuItem>
                {species.map((s) => (
                  <MenuItem key={s.species_id} value={s.species_id}>
                    {s.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
        )}
        {showCompetencyType && (
          <Grid size={{ xs: 12, sm: 6 }}>
            <FormControl fullWidth>
              <InputLabel>Competency type</InputLabel>
              <Select
                label="Competency type"
                value={processing.requirement_type}
                onChange={(e) => updateField("requirement_type", e.target.value)}
              >
                <MenuItem value={REQUIREMENT_COMPETENCY_TYPE.EPA}>
                  {REQUIREMENT_COMPETENCY_TYPE.EPA}
                </MenuItem>
                <MenuItem value={REQUIREMENT_COMPETENCY_TYPE.DOP}>
                  {REQUIREMENT_COMPETENCY_TYPE.DOP}
                </MenuItem>
              </Select>
            </FormControl>
          </Grid>
        )}
        <Grid size={{ xs: 6, sm: 3 }}>
          <TextField
            label="Min attempts"
            type="number"
            fullWidth
            value={processing.min_performance}
            onChange={(e) =>
              updateField(
                "min_performance",
                Number.parseInt(e.target.value, 10) || 0,
              )
            }
            inputProps={{ min: 0 }}
          />
        </Grid>
        <Grid size={{ xs: 6, sm: 3 }}>
          <TextField
            label="Min score"
            type="number"
            fullWidth
            value={processing.min_score}
            onChange={(e) =>
              updateField("min_score", Number.parseInt(e.target.value, 10) || 0)
            }
            inputProps={{ min: 0 }}
          />
        </Grid>
        <Grid size={{ xs: 6, sm: 3 }}>
          <TextField
            label="Small animal min"
            type="number"
            fullWidth
            value={processing.min_small_animals_count}
            onChange={(e) =>
              updateField(
                "min_small_animals_count",
                Number.parseInt(e.target.value, 10) || 0,
              )
            }
            inputProps={{ min: 0 }}
          />
        </Grid>
        <Grid size={{ xs: 6, sm: 3 }}>
          <TextField
            label="Large animal min"
            type="number"
            fullWidth
            value={processing.min_large_animals_count}
            onChange={(e) =>
              updateField(
                "min_large_animals_count",
                Number.parseInt(e.target.value, 10) || 0,
              )
            }
            inputProps={{ min: 0 }}
          />
        </Grid>
      </Grid>
        </>
      )}

      <Box my={2} mt={4}>
        <Typography variant="h6">Supplemental Information</Typography>
        <Divider sx={{ mt: 1 }} />
      </Box>

      <RelevantResourcesLink
        relevantResourceLinks={processing.resources}
        onChange={handleResourcesChange}
      />

      <Box my={2} mt={4}>
        <Typography variant="h6">Tags</Typography>
        <Divider sx={{ mt: 1 }} />
      </Box>

      <TagManager
        tags={processing.tags}
        onAdd={handleAddTag}
        onDelete={handleDeleteTag}
        isEditing
      />

      {showRubricLabels && (
        <>
      <Typography variant="h6" mt={4} mb={1}>
        Rubric labels
      </Typography>
      <Typography variant="body2" color="text.secondary" mb={2}>
        CSV rubric columns map to positions 1–6 in order. Set the display labels
        for skill levels and target achievement times below.
      </Typography>
      <Grid container spacing={3}>
        <Grid size={{ xs: 12, md: 6 }}>
          <Box
            sx={{
              border: 1,
              borderColor: "divider",
              borderRadius: 1,
              p: 2,
            }}
          >
            <Typography variant="subtitle1" gutterBottom>
              Skill level labels
            </Typography>
            <Typography variant="body2" color="text.secondary" mb={2}>
              Applied to rubric columns 1–6 by skill level score
            </Typography>
            <Grid container spacing={2}>
              {Array.from({ length: RUBRIC_COLUMN_COUNT }).map((_, index) => (
                <Grid size={{ xs: 12 }} key={`skill-label-${index + 1}`}>
                  <TextField
                    label={`Level ${index + 1}`}
                    fullWidth
                    size="small"
                    value={processing.rubric_skill_level_labels[index] || ""}
                    onChange={(e) =>
                      updateRubricLabel(
                        "rubric_skill_level_labels",
                        index,
                        e.target.value,
                      )
                    }
                  />
                </Grid>
              ))}
            </Grid>
          </Box>
        </Grid>
        <Grid size={{ xs: 12, md: 6 }}>
          <Box
            sx={{
              border: 1,
              borderColor: "divider",
              borderRadius: 1,
              p: 2,
            }}
          >
            <Typography variant="subtitle1" gutterBottom>
              Target achievement time labels
            </Typography>
            <Typography variant="body2" color="text.secondary" mb={2}>
              Applied to rubric columns 1–6 by time sequence
            </Typography>
            <Grid container spacing={2}>
              {Array.from({ length: RUBRIC_COLUMN_COUNT }).map((_, index) => (
                <Grid size={{ xs: 12 }} key={`time-label-${index + 1}`}>
                  <TextField
                    label={`Time ${index + 1}`}
                    fullWidth
                    size="small"
                    value={processing.rubric_time_labels[index] || ""}
                    onChange={(e) =>
                      updateRubricLabel(
                        "rubric_time_labels",
                        index,
                        e.target.value,
                      )
                    }
                  />
                </Grid>
              ))}
            </Grid>
          </Box>
        </Grid>
      </Grid>
        </>
      )}
    </StepLayoutContainer>
  );
};
